import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import { Badge } from '../../../components/ui/badge';
import { useBillDuplicateCheck } from '../hooks/useAp';
import { LocalizedDateDisplay } from '../../../shared/components';
import { LtrToken } from '../../../shared/components/common';
import { LocalizedCurrencyDisplay } from '../../../shared/components/LocalizedCurrencyDisplay';
import { getEntityUrl } from '../../../shared/utils/entityUrls';

interface Props {
  vendorId?: string;
  vendorInvoiceNumber?: string;
  total: number;
  issueDate?: string;
  excludeBillId?: string;
  currencyCode: string;
}

export function DuplicateBillWarning({
  vendorId,
  vendorInvoiceNumber,
  total,
  issueDate,
  excludeBillId,
  currencyCode,
}: Props) {
  const { t } = useTranslation(['accounting', 'common']);
  const { data } = useBillDuplicateCheck(
    total > 0
      ? {
          vendor_id: vendorId,
          vendor_invoice_number: vendorInvoiceNumber,
          total,
          issue_date: issueDate,
          exclude_bill_id: excludeBillId,
        }
      : null,
  );

  if (!data?.data?.has_duplicates) return null;
  const matches = data.data.matches;

  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{t('duplicateCheck.warningTitle')}</AlertTitle>
      <AlertDescription>
        <p className="mb-2">{t('duplicateCheck.warningMessage')}</p>
        <ul className="space-y-1.5">
          {matches.map((m) => (
            <li key={m.bill_id} className="flex items-center gap-2 text-sm">
              <Badge variant="outline" className="text-xs">
                {m.confidence === 'exact'
                  ? t('duplicateCheck.matchExact')
                  : t('duplicateCheck.matchFuzzy')}
              </Badge>
              <Link
                to={
                  getEntityUrl({
                    entityType: 'accounting_bills',
                    entityId: m.bill_id,
                  }) ?? '#'
                }
                className="font-medium underline hover:no-underline"
              >
                <LtrToken>{m.bill_number}</LtrToken>
              </Link>
              {m.vendor_invoice_number && (
                <span className="text-muted-foreground">
                  <LtrToken>({m.vendor_invoice_number})</LtrToken>
                </span>
              )}
              {m.issue_date && (
                <span className="text-muted-foreground">
                  · <LocalizedDateDisplay dateOnly date={m.issue_date} />
                </span>
              )}
              <span className="text-muted-foreground inline-flex items-center gap-1">
                ·{' '}
                <LocalizedCurrencyDisplay
                  value={m.total}
                  currency={currencyCode}
                />
              </span>
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
